export const name = "TO_CODE_POINTS";
export const summary = `Converts a \`STRING\` or \`BYTES\` value into an array of extended ASCII code points.`;
export const reference = `
## TO_CODE_POINTS

Converts a \`STRING\` or \`BYTES\` value into an array of extended ASCII code points.

\`\`\`
TO_CODE_POINTS(value)
\`\`\`

### Description

Takes a \`STRING\` or \`BYTES\` value and returns an array of \`INT64\` values that represent code points or extended ASCII character values.

- If \`value\` is a \`STRING\`, each element in the returned array represents a code point. Each code point falls within the range of [0, 0xD7FF] and [0xE000, 0x10FFFF].
- If \`value\` is \`BYTES\`, each element in the array is an extended ASCII character value in the range of [0, 255].

To convert from an array of code points to a \`STRING\` or \`BYTES\`, see CODE_POINTS_TO_STRING or CODE_POINTS_TO_BYTES.


### Return type

\`ARRAY<INT64>\`


### Examples

The following examples get the code points for each element in an array of words.



\`\`\`
SELECT
  'foo' AS word,
  TO_CODE_POINTS('foo') AS code_points

/*---------+------------------------------------*
 | word | code_points |
 +---------+------------------------------------+
 | foo | [102, 111, 111] |
 *---------+------------------------------------*/
\`\`\`



\`\`\`
SELECT
  'bar' AS word,
  TO_CODE_POINTS('bar') AS code_points

/*---------+------------------------------------*
 | word | code_points |
 +---------+------------------------------------+
 | bar | [98, 97, 114] |
 *---------+------------------------------------*/
\`\`\`



\`\`\`
SELECT
  'baz' AS word,
  TO_CODE_POINTS('baz') AS code_points

/*---------+------------------------------------*
 | word | code_points |
 +---------+------------------------------------+
 | baz | [98, 97, 122] |
 *---------+------------------------------------*/
\`\`\`



\`\`\`
SELECT
  'giraffe' AS word,
  TO_CODE_POINTS('giraffe') AS code_points

/*---------+------------------------------------*
 | word | code_points |
 +---------+------------------------------------+
 | giraffe | [103, 105, 114, 97, 102, 102, 101] |
 *---------+------------------------------------*/
\`\`\`



\`\`\`
SELECT
  'llama' AS word,
  TO_CODE_POINTS('llama') AS code_points

/*---------+------------------------------------*
 | word | code_points |
 +---------+------------------------------------+
 | llama | [108, 108, 97, 109, 97] |
 *---------+------------------------------------*/
\`\`\`

The following examples convert integer representations of \`BYTES\` to their corresponding ASCII character values.



\`\`\`
SELECT
  b'\\x66\\x6f\\x6f' AS bytes_value,
  TO_CODE_POINTS(b'\\x66\\x6f\\x6f') AS bytes_value_as_integer

/*------------------+------------------------*
 | bytes_value | bytes_value_as_integer |
 +------------------+------------------------+
 | foo | [102, 111, 111] |
 *------------------+------------------------*/
\`\`\`



\`\`\`
SELECT
  b'\\x00\\x01\\x10\\xff' AS bytes_value,
  TO_CODE_POINTS(b'\\x00\\x01\\x10\\xff') AS bytes_value_as_integer

/*------------------+------------------------*
 | bytes_value | bytes_value_as_integer |
 +------------------+------------------------+
 | \\x00\\x01\\x10\\xff | [0, 1, 16, 255] |
 *------------------+------------------------*/
\`\`\`

The following example demonstrates the difference between a \`BYTES\` result and a\`STRING\` result. Notice that the character \`Ā\` is represented as a two-byte Unicode sequence. As a result, the \`BYTES\` version of \`TO_CODE_POINTS\` returns an array with two elements, while the \`STRING\` version returns an array with a single element.



\`\`\`
SELECT TO_CODE_POINTS(b'Ā') AS b_result, TO_CODE_POINTS('Ā') AS s_result;

/*------------+----------*
 | b_result | s_result |
 +------------+----------+
 | [196, 128] | [256] |
 *------------+----------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/string_functions
`;
